/**
 * Tur PDF Düzenleme ekranının saf mantığı (React ve DOM yok; `api/test/unit/tur-pdf-ui-mantik.spec.ts` koşturur).
 */

/** Operasyon başlığındaki eylem düğmeleri. Sıra bilinçli: "Belge oluştur"un hemen yanında "PDF düzenle". */
export type OperasyonBaslikEylemi = 'create-document' | 'tur-pdf';

export const BASLIK_EYLEMLERI: readonly { key: OperasyonBaslikEylemi; izin: string; testId: string }[] = [
  { key: 'create-document', izin: 'visa-document:generate', testId: 'ops-new-document' },
  { key: 'tur-pdf', izin: 'tour-pdf:process', testId: 'ops-tur-pdf' },
];

/** Her eylem kendi izniyle görünür; izni olmayan rolde düğme hiç çizilmez ("Belge oluştur" ile aynı kural). */
export function gorunenBaslikEylemleri(izinler: readonly string[]): readonly (typeof BASLIK_EYLEMLERI)[number][] {
  return BASLIK_EYLEMLERI.filter((eylem) => izinler.includes(eylem.izin));
}

export interface KuralSatiri {
  ara: string;
  yeni: string;
}

export const AZAMI_KURAL = 50;
export const AZAMI_KURAL_METNI = 2000;

/** Profil formundaki kural listesinin kayıttan önceki denetimi; sunucu aynı kuralı yeniden uygular. */
export function kuralHatasi(kurallar: readonly KuralSatiri[]): string | null {
  if (kurallar.length < 1 || kurallar.length > AZAMI_KURAL) return `1 ile ${AZAMI_KURAL} arasında kural girin.`;
  const gorulen = new Set<string>();
  for (const kural of kurallar) {
    if (kural.ara.trim() === '') return 'Her kuralda aranan metin bulunmalı.';
    if (kural.ara.length > AZAMI_KURAL_METNI || kural.yeni.length > AZAMI_KURAL_METNI) return `Kural metni ${AZAMI_KURAL_METNI} karakteri aşamaz.`;
    const anahtar = kural.ara.toLocaleLowerCase('tr-TR');
    if (gorulen.has(anahtar)) return 'Aynı metin iki kuralda aranamaz. Çakışan kuralı kaldırın.';
    gorulen.add(anahtar);
  }
  return null;
}

export function sayfaHatasi(deger: string, ad: string, ustSinir: number | null = null): string | null {
  if (!/^\d{1,5}$/.test(deger.trim()) || Number(deger) < 1) return `${ad} 1 veya daha büyük bir tam sayı olmalı.`;
  if (ustSinir !== null && Number(deger) > ustSinir) return `${ad} en çok ${ustSinir} olabilir.`;
  return null;
}

export interface IslemDurumu {
  belgeSecili: boolean;
  profilSecili: boolean;
  kuralSayisi: number;
}

/** İşle düğmelerinin kapalı olma nedeni; null ise açık. */
export function islemEngeli(durum: IslemDurumu, metin: { engelBelge: string; engelProfil: string; engelKuralYok: string }): string | null {
  if (!durum.belgeSecili) return metin.engelBelge;
  if (!durum.profilSecili) return metin.engelProfil;
  if (durum.kuralSayisi < 1) return metin.engelKuralYok;
  return null;
}

/** Tekrar denemede aynı anahtar: giriş değişmedikçe aynı `processKey`; değişince yenisi. */
export function islemAnahtari(onceki: { imza: string; anahtar: string } | null, imza: string, uret: () => string): { imza: string; anahtar: string } {
  return onceki && onceki.imza === imza ? onceki : { imza, anahtar: uret() };
}

export function dosyaBase64(bayt: ArrayBuffer): string {
  let metin = '';
  const dizi = new Uint8Array(bayt);
  for (let i = 0; i < dizi.length; i += 0x8000) metin += String.fromCharCode(...dizi.subarray(i, i + 0x8000));
  return btoa(metin);
}

export const AZAMI_KB_SAYFA_BASI = 2000;
export const EN_AZ_KB_SAYFA_BASI = 100;

/** Sayfa basi ust sinir (KB; 1 KB = 1024 bayt): 100-2000 tam sayi. */
export function kbHatasi(deger: string): string | null {
  if (!/^\d{1,5}$/.test(deger.trim()) || Number(deger) < EN_AZ_KB_SAYFA_BASI || Number(deger) > AZAMI_KB_SAYFA_BASI) {
    return `Sayfa başı üst sınır ${EN_AZ_KB_SAYFA_BASI} ile ${AZAMI_KB_SAYFA_BASI} KB arasında tam sayı olmalı (1 KB = 1024 bayt).`;
  }
  return null;
}

/** 4_883_456 -> "4,7 MB" (1 MB = 1024 x 1024 bayt), Türkçe ondalık virgülü. */
export function mbMetni(bayt: number): string {
  return `${(bayt / (1024 * 1024)).toLocaleString('tr-TR', { minimumFractionDigits: 1, maximumFractionDigits: 1 })} MB`;
}

/** Çıktının sınırı: çıktıdaki sayfa sayısı x sayfa başı KB x 1024 bayt. Hesabı asıl yapan Python servisi; ekran yalnız gösterir. */
export function sinirBayti(sayfaSayisi: number, kbSayfaBasi: number): number {
  return sayfaSayisi * kbSayfaBasi * 1024;
}

/** Sıkıştırma uygulandıysa bildirim metni, yoksa null. Sayılar sunucudan gelir. */
export function sikistirmaBildirimi(bilgi: { step: number; originalBytes: number; outputBytes: number } | null | undefined): string | null {
  if (!bilgi || bilgi.step < 1 || bilgi.originalBytes <= bilgi.outputBytes) return null;
  return `Çıktı sıkıştırıldı (${mbMetni(bilgi.originalBytes)} -> ${mbMetni(bilgi.outputBytes)}).`;
}

/** Ek yükleme sonrası bildirim; özgün dosyanın saklanmadığını açıkça söyler. */
export function ekOptimizasyonBildirimi(bilgi: { originalBytes: number; outputBytes: number } | null | undefined): string | null {
  if (!bilgi || bilgi.originalBytes <= bilgi.outputBytes) return null;
  return `Ek optimize edildi (${mbMetni(bilgi.originalBytes)} -> ${mbMetni(bilgi.outputBytes)}). Özgün dosya saklanmaz; yalnız optimize hali kalıcıdır.`;
}
